import { Card, CardBody, Input, Tooltip } from '@hawkins/components';
import { useState, useEffect, useMemo } from 'react';
import { ceiling } from 'src/domains/calculators/utilities/ceiling';
import { HorizontalInputStack } from './HorizontalInputStack';
import styles from './CustomResolutionInput.module.css';
import { ResolutionInput } from '../FramingChart/types';
import { getCustomFramingDecisionValidation } from '../utilities/getCustomFramingDecisionValidation';
import { Resolution } from 'src/domains/calculators/utilities/types';

type CustomResolutionInputProps = {
  customHeight: number | null;
  customWidth: number | null;
  onChange: ({ width, height }: ResolutionInput) => void;
  canvasResolution?: Resolution | null;
};

/**
 * CustomResolutionInput component allows users to input custom width and height values.
 * Used for custom camera sensor resolutions and effective canvase resolutions.
 * Values are rounded up to the nearest even number on blur.
 */
export const CustomResolutionInput = ({
  customHeight,
  customWidth,
  onChange,
  canvasResolution,
}: CustomResolutionInputProps) => {
  const [inputWidth, setInputWidth] = useState(customWidth?.toString() ?? '');
  const [inputHeight, setInputHeight] = useState(
    customHeight?.toString() ?? ''
  );

  const [invalidWidth, setInvalidWidth] = useState(false);
  const [invalidHeight, setInvalidHeight] = useState(false);

  const canvasValidation = useMemo(
    () =>
      canvasResolution != null
        ? getCustomFramingDecisionValidation(
            inputWidth,
            inputHeight,
            canvasResolution,
            'Canvas'
          )
        : null,
    [inputWidth, inputHeight, canvasResolution]
  );

  const widthInvalid =
    canvasValidation != null
      ? canvasValidation.widthWarning != null
      : invalidWidth;
  const heightInvalid =
    canvasValidation != null
      ? canvasValidation.heightWarning != null
      : invalidHeight;
  const widthValidationMessage =
    canvasValidation?.widthWarning ??
    (invalidWidth ? 'Invalid width' : undefined);
  const heightValidationMessage =
    canvasValidation?.heightWarning ??
    (invalidHeight ? 'Invalid height' : undefined);

  useEffect(() => {
    setInputWidth(customWidth?.toString() ?? '');
  }, [customWidth]);

  useEffect(() => {
    setInputHeight(customHeight?.toString() ?? '');
  }, [customHeight]);

  const roundingMessage = 'Value is rounded up to the nearest even number';

  return (
    <Card>
      <CardBody className={styles.customResolutionInputContainer}>
        <HorizontalInputStack>
          <Tooltip label={roundingMessage} placement="bottom">
            <Input
              validationStatus={widthInvalid ? 'error' : undefined}
              validationMessage={widthValidationMessage}
              label="Width"
              type="number"
              fullWidth
              value={inputWidth}
              onChange={(event) => {
                const value = event.target.value;
                setInputWidth(value);
                // Set validation state for input
                const width = Number(value);
                setInvalidWidth(value === '' || width <= 0);
              }}
              onBlur={() => {
                const rawWidth = Number(inputWidth);
                if (rawWidth > 0) {
                  const roundedWidth = ceiling(rawWidth, 2);
                  setInputWidth(roundedWidth.toString());
                  onChange({
                    height: customHeight, // keep height unchanged
                    width: roundedWidth,
                  });
                }
              }}
            />
          </Tooltip>
          <Tooltip label={roundingMessage} placement="bottom">
            <Input
              validationStatus={heightInvalid ? 'error' : undefined}
              validationMessage={heightValidationMessage}
              label="Height"
              type="number"
              fullWidth
              value={inputHeight}
              onChange={(event) => {
                const value = event.target.value;
                setInputHeight(value);
                // Set validation state for input
                const height = Number(value);
                setInvalidHeight(value === '' || height <= 0);
              }}
              onBlur={() => {
                const rawHeight = Number(inputHeight);
                if (rawHeight > 0) {
                  const roundedHeight = ceiling(rawHeight, 2);
                  setInputHeight(roundedHeight.toString());
                  onChange({
                    height: roundedHeight,
                    width: customWidth, // keep width unchanged
                  });
                }
              }}
            />
          </Tooltip>
        </HorizontalInputStack>
      </CardBody>
    </Card>
  );
};
